/**
 * Line diff for showing a proposed prompt edit as A / M / D rows.
 *
 * Rows:
 *   ctx — unchanged line kept for context
 *   A   — added line
 *   D   — deleted line
 *   M   — modified line (a deletion paired with the addition that replaced it;
 *         `oldText` carries the original)
 *   gap — elided unchanged lines between hunks
 */
export type DiffRow =
  | { kind: 'ctx'; text: string }
  | { kind: 'A'; text: string }
  | { kind: 'D'; text: string }
  | { kind: 'M'; text: string; oldText: string }
  | { kind: 'gap'; count: number };

type Op = { kind: 'eq' | 'add' | 'del'; text: string };

const splitLines = (text: string) => (text === '' ? [] : text.replace(/\r\n/g, '\n').split('\n'));

/** Longest-common-subsequence edit script; inputs are prompt-sized (hundreds of lines at most). */
const editScript = (before: string[], after: string[]): Op[] => {
  const n = before.length;
  const m = after.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = before[i] === after[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      ops.push({ kind: 'eq', text: before[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ kind: 'del', text: before[i] });
      i++;
    } else {
      ops.push({ kind: 'add', text: after[j] });
      j++;
    }
  }
  while (i < n) ops.push({ kind: 'del', text: before[i++] });
  while (j < m) ops.push({ kind: 'add', text: after[j++] });
  return ops;
};

/** Pair each run of deletions with the additions that follow it into M rows. */
const pairModifications = (ops: Op[]): Exclude<DiffRow, { kind: 'gap' }>[] => {
  const rows: Exclude<DiffRow, { kind: 'gap' }>[] = [];
  let k = 0;
  while (k < ops.length) {
    if (ops[k].kind === 'eq') {
      rows.push({ kind: 'ctx', text: ops[k].text });
      k++;
      continue;
    }
    const dels: string[] = [];
    const adds: string[] = [];
    while (k < ops.length && ops[k].kind !== 'eq') {
      (ops[k].kind === 'del' ? dels : adds).push(ops[k].text);
      k++;
    }
    const paired = Math.min(dels.length, adds.length);
    for (let p = 0; p < paired; p++) rows.push({ kind: 'M', text: adds[p], oldText: dels[p] });
    for (let p = paired; p < dels.length; p++) rows.push({ kind: 'D', text: dels[p] });
    for (let p = paired; p < adds.length; p++) rows.push({ kind: 'A', text: adds[p] });
  }
  return rows;
};

/** Keep `context` unchanged lines around each change; elide the rest as gaps. */
const elideContext = (rows: Exclude<DiffRow, { kind: 'gap' }>[], context: number): DiffRow[] => {
  const keep = new Array<boolean>(rows.length).fill(false);
  rows.forEach((row, idx) => {
    if (row.kind === 'ctx') return;
    for (let c = Math.max(0, idx - context); c <= Math.min(rows.length - 1, idx + context); c++) keep[c] = true;
  });
  const out: DiffRow[] = [];
  let gap = 0;
  rows.forEach((row, idx) => {
    if (keep[idx]) {
      if (gap > 0) out.push({ kind: 'gap', count: gap });
      gap = 0;
      out.push(row);
    } else {
      gap++;
    }
  });
  if (gap > 0) out.push({ kind: 'gap', count: gap });
  return out;
};

export const lineDiff = (before: string, after: string, context = 2): DiffRow[] =>
  elideContext(pairModifications(editScript(splitLines(before), splitLines(after))), context);

export const hasChanges = (rows: DiffRow[]) => rows.some((r) => r.kind === 'A' || r.kind === 'D' || r.kind === 'M');
